import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useEffect, useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Debounced free-text search input (PLAN §3.3).
 *
 * Fires `onSearch` only after the value settles for `debounceMs` AND
 * meets `minLength` (or is empty — clearing always fires so the page can
 * drop the filter). `minLength` defaults to 3 to match the backend's
 * min-3-char ILIKE guard (§1.5) that prevents runaway table scans.
 */
export interface SearchInputProps {
  onSearch: (query: string) => void;
  /** Minimum chars before a non-empty query fires. */
  minLength?: number;
  debounceMs?: number;
  /** Controlled initial value (e.g. hydrated from the URL). */
  initialValue?: string;
  placeholder?: string;
  /** `data-testid` for the native `<input>` (e2e hooks). */
  testId?: string;
}

export const SearchInput = ({
  onSearch,
  minLength = 3,
  debounceMs = 350,
  initialValue = '',
  placeholder,
  testId,
}: SearchInputProps): ReactElement => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => {
    const trimmed = value.trim();
    const handle = window.setTimeout(() => {
      if (trimmed.length === 0 || trimmed.length >= minLength) {
        onSearchRef.current(trimmed);
      }
    }, debounceMs);
    return () => window.clearTimeout(handle);
  }, [value, minLength, debounceMs]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setValue(e.target.value);
  };

  return (
    <TextField
      size="small"
      value={value}
      onChange={handleChange}
      placeholder={placeholder ?? t('filters.searchPlaceholder')}
      slotProps={{
        htmlInput: {
          'aria-label': t('filters.searchLabel'),
          ...(testId ? { 'data-testid': testId } : {}),
        },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
      sx={{ minWidth: 240 }}
    />
  );
};
